const pendingByRoot = new WeakMap();
const slowFeedbackByStatus = new WeakMap();

/**
 * Says where the reader has landed, into the pagination component's own live region.
 *
 * Shared because two modules swap paginated markup and the sentence has to be the same one: the general
 * AJAX pager, and the Steam review screen, which takes its page steps through its own card loader and
 * would otherwise announce a page in wording of its own - or, worse, drift out of step with this one
 * when either is edited.
 *
 * The scopes are tried in order, so a caller can prefer the swapped-in region over an outer one. Values
 * arrive either as numbers (parsed from a fragment) or as strings (read back off `data-*` attributes),
 * and an absent one means there is nothing to announce rather than a `null` to print.
 *
 * @param {Array<{querySelector?: Function}|null|undefined>} scopes
 * @param {{pageLabel?: string, currentPage?: number|string|null, totalPages?: number|string|null}} position
 */
export function announcePaginationPosition(scopes, position) {
    const pageLabel = String(position?.pageLabel ?? '').trim();
    const currentPage = String(position?.currentPage ?? '').trim();
    const totalPages = String(position?.totalPages ?? '').trim();
    if (pageLabel === '' || currentPage === '' || totalPages === '') {
        return;
    }

    for (const scope of scopes) {
        const announcement = scope?.querySelector?.('[data-pagination-announcement]');
        if (announcement instanceof HTMLElement) {
            announcement.textContent = `${pageLabel}, page ${currentPage} of ${totalPages}`;

            return;
        }
    }
}

export function acquirePaginationPending(root, target) {
    if (
        !(root instanceof HTMLElement)
        || !(target instanceof HTMLElement)
        || !target.isConnected
        || !root.contains(target)
        || target.getAttribute('data-page-number') === null
    ) {
        return () => {};
    }

    const token = Symbol('pagination-pending');
    const previous = pendingByRoot.get(root);
    previous?.target.classList.remove('is-pending');

    root.classList.add('has-pending-pagination');
    target.classList.add('is-pending');
    pendingByRoot.set(root, { target, token });

    return () => {
        const current = pendingByRoot.get(root);
        if (current?.token !== token) {
            return;
        }

        target.classList.remove('is-pending');
        root.classList.remove('has-pending-pagination');
        pendingByRoot.delete(root);
    };
}

export function acquirePaginationSlowFeedback(nav, timerApi = globalThis) {
    const status = nav instanceof HTMLElement
        ? nav.querySelector('[data-pagination-slow-status]')
        : null;
    if (
        !(status instanceof HTMLElement)
        || typeof timerApi?.setTimeout !== 'function'
        || typeof timerApi?.clearTimeout !== 'function'
    ) {
        return () => {};
    }

    const token = Symbol('pagination-slow-feedback');
    const previous = slowFeedbackByStatus.get(status);
    if (previous !== undefined) {
        previous.timerApi.clearTimeout(previous.timerId);
    }

    status.hidden = true;
    const timerId = timerApi.setTimeout(() => {
        if (slowFeedbackByStatus.get(status)?.token === token) {
            status.hidden = false;
        }
    }, 1400);
    slowFeedbackByStatus.set(status, { timerApi, timerId, token });

    return () => {
        const current = slowFeedbackByStatus.get(status);
        if (current?.token !== token) {
            return;
        }

        timerApi.clearTimeout(timerId);
        status.hidden = true;
        slowFeedbackByStatus.delete(status);
    };
}
